/**
 * Screen-share source picking.
 *
 * Rafeekh already toggles screen share (apps/mobile call_page.dart:618), but
 * with no control over WHAT gets shared: the browser's default picker opens
 * on whichever tab it feels like. Meet's ScreenSharePicker lets the user
 * start on the right surface.
 *
 * There is no way to enumerate windows from JavaScript -- that would be a
 * privacy hole, and no browser exposes it. `preferred` sets which tab of the
 * browser's own picker opens first; the user still chooses.
 */

import type { ScreenShareApi, ScreenShareOptions } from './types.js';

/**
 * Chrome's `preferCurrentTab` / `selfBrowserSurface` and the standard
 * `displaySurface` hint are not in every lib.dom.d.ts, and are silently
 * ignored by browsers that do not implement them, which is the behaviour we
 * want. Typed loosely here rather than casting at each call site.
 */
interface DisplayMediaConstraints {
  video: boolean | { displaySurface?: string };
  audio: boolean;
  selfBrowserSurface?: string;
  systemAudio?: string;
}

function mediaDevices(): MediaDevices | null {
  if (typeof navigator === 'undefined') return null;
  return navigator.mediaDevices ?? null;
}

export const screenShare: ScreenShareApi = {
  isSupported(): boolean {
    const md = mediaDevices();
    return !!md && typeof md.getDisplayMedia === 'function';
  },

  async pick(options: ScreenShareOptions = {}): Promise<MediaStream | null> {
    const md = mediaDevices();
    if (!md || typeof md.getDisplayMedia !== 'function') {
      // iOS Safari has no getDisplayMedia at all. Returning null lets the
      // caller hide the control rather than show one that always errors.
      return null;
    }

    const constraints: DisplayMediaConstraints = {
      video: options.preferred ? { displaySurface: options.preferred } : true,
      audio: options.withAudio === true,
    };
    if (options.selfBrowserSurface) {
      constraints.selfBrowserSurface = options.selfBrowserSurface;
    }
    if (options.withAudio) {
      constraints.systemAudio = 'include';
    }

    try {
      return await md.getDisplayMedia(constraints as unknown as DisplayMediaStreamOptions);
    } catch (err) {
      // The user pressing Cancel arrives here as NotAllowedError, identical
      // to a policy denial. Both mean "no stream", and neither is an error
      // the user needs to see -- they just decided not to share.
      const name = (err as { name?: string } | null)?.name;
      if (name === 'NotAllowedError' || name === 'AbortError' || name === 'NotFoundError') {
        return null;
      }
      throw err;
    }
  },
};
